import { Icon } from '@clientbase/clientbase-library'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Box,
  Checkbox,
  Skeleton,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import { customFieldsMapper } from 'templates/v2/RegistrationLink/components/Info/ExtraInfo/components/ExtraFieldsConfiguration/components/ExtraFieldsAutocomplete'

import { theme } from 'styles/theme'

import { ExtraFieldItem } from './index'

// Skeleton da linha enquanto um campo novo está sendo criado
export const ExtraFieldSkeleton = () => {
  return (
    <Box mb={1}>
      <Stack direction="row" alignItems="center" gap={2}>
        <Skeleton
          variant="rectangular"
          sx={{ flexGrow: 1, borderRadius: 1 }}
          height={36}
        />

        <Skeleton variant="circular" width={18} height={18} />
      </Stack>
    </Box>
  )
}

interface SortableExtraFieldItemProps {
  id: string
  extraField: ExtraFieldItem
  onToggleRequired: (id: string, checked: boolean) => void
  onDelete: (id: string) => void
  // Sem onEdit a linha não é clicável (ex.: usuário sem custom_fields:manage)
  onEdit?: (id: string) => void
  // Configurações extras da linha, abaixo do "Tornar obrigatório"
  children?: React.ReactNode
}

// Linha de campo extra configurado num link: arrastar, editar, remover e
// marcar como obrigatório. Usar dentro de um SortableContext do dnd-kit.
const SortableExtraFieldItem = ({
  id,
  extraField,
  onToggleRequired,
  onDelete,
  onEdit,
  children
}: SortableExtraFieldItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })

  const style = {
    transform: transform ? CSS.Transform.toString(transform) : undefined,
    transition,
    zIndex: isDragging ? 1000 : 1,
    backgroundColor: isDragging ? theme.palette.neutral['A100'] : 'transparent',
    boxShadow: isDragging ? '0px 4px 8px rgba(0, 0, 0, 0.1)' : 'none',
    borderRadius: '8px',
    position: 'relative' as const,
    marginBottom: '8px',
    paddingTop: '8px'
  }

  return (
    <Stack ref={setNodeRef} style={style}>
      <Stack direction="row">
        <Stack ml="-8px" {...attributes} {...listeners}>
          <Icon
            sx={{
              mt: '25%',
              cursor: 'grab',
              color: theme.palette.neutral[600]
            }}
            icon="moreVert"
          />
        </Stack>

        <Stack
          width="100%"
          padding="8px"
          borderRadius="8px"
          border={`1px solid ${theme.palette.neutral[600]}`}
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="8px"
          {...(onEdit && {
            role: 'button',
            tabIndex: 0,
            'aria-label': `Editar campo ${extraField.name}`,
            onClick: () => onEdit(id),
            onKeyDown: (e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onEdit(id)
              }
            },
            sx: {
              cursor: 'pointer',
              transition: 'border-color 150ms ease',
              '& .extra-field-edit-icon': {
                color: theme.palette.neutral[600],
                transition: 'color 150ms ease'
              },
              '&:hover, &:focus-visible': {
                outline: 'none',
                borderColor: theme.palette.neutral[400]
              },
              '&:hover .extra-field-edit-icon, &:focus-visible .extra-field-edit-icon':
                { color: theme.palette.neutral[200] }
            }
          })}
        >
          <Stack direction="row" gap="8px" minWidth={0}>
            <Typography color={theme.palette.neutral[200]} variant="smLight">
              {extraField.name}
            </Typography>
            <Typography color={theme.palette.neutral[400]} variant="smLight">
              -{' '}
              {customFieldsMapper[
                extraField.field_type as keyof typeof customFieldsMapper
              ] || 'Texto curto'}
            </Typography>
          </Stack>
          {onEdit && (
            <Tooltip title="Editar campo" arrow placement="top">
              <Stack flexShrink={0}>
                <Icon
                  icon="edit"
                  className="extra-field-edit-icon"
                  sx={{ fontSize: '16px' }}
                />
              </Stack>
            </Tooltip>
          )}
        </Stack>
        <Stack>
          <Icon
            onClick={() => onDelete(id)}
            icon="close"
            sx={{
              mt: '8px',
              ml: '8px',
              cursor: 'pointer',
              color: theme.palette.neutral[600],
              fontSize: '16px'
            }}
          />
        </Stack>
      </Stack>
      <Box display="flex" alignItems="center" ml="5px">
        <Checkbox
          checked={extraField.required}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            onToggleRequired(id, e.target.checked)
          }}
          sx={{
            marginLeft: '5px'
          }}
          size="small"
          id={`required-checkbox-${id}`}
        />
        <label
          htmlFor={`required-checkbox-${id}`}
          style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        >
          <Typography variant="smLight" color={theme.palette.neutral[400]}>
            Tornar obrigatório
          </Typography>
        </label>
      </Box>
      {children}
    </Stack>
  )
}

export default SortableExtraFieldItem
